import { useEffect } from "react";

export interface EducationalProgramProps {
  name: string;
  description: string;
  url: string;
  educationalLevel?: string;
  timeToComplete?: string;
}

export interface EducationalOrganizationSchemaProps {
  name: string;
  description: string;
  url: string;
  logo?: string;
  email?: string;
  foundingDate?: string;
  areaServed?: string[];
  knowsLanguage?: string[];
  numberOfStudents?: number;
  programs?: EducationalProgramProps[];
}

/**
 * EducationalOrganizationSchema for the Academy section
 * Establishes Data Hire Club as an educational institution
 */
export function EducationalOrganizationSchema({
  name = "Data Hire Club Academy",
  description = "Plataforma de aprendizado para profissionais de dados com trilhas de estudo, vídeo-aulas, simulações de entrevista e certificados",
  url = "https://datahireclub.com.br/candidate/practice",
  logo = "https://datahireclub.com.br/favicon.png",
  email = "contato@datahireclub.com.br",
  foundingDate = "2024-01-01",
  areaServed = ["BR", "PT"],
  knowsLanguage = ["pt-BR"],
  numberOfStudents,
  programs = []
}: EducationalOrganizationSchemaProps) {
  useEffect(() => {
    const schemaId = "educational-organization-schema";
    
    // Remove existing schema
    const existingSchema = document.getElementById(schemaId);
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": name,
      "description": description,
      "url": url,
      "logo": logo,
      "email": email,
      "foundingDate": foundingDate,
      "areaServed": areaServed,
      "knowsLanguage": knowsLanguage,
      "hasCredential": {
        "@type": "EducationalOccupationalCredential",
        "name": "Certificado Data Hire Club",
        "credentialCategory": "Professional Certificate"
      }
    };

    if (numberOfStudents) {
      schema.alumni = {
        "@type": "QuantitativeValue",
        "value": numberOfStudents
      };
    }

    if (programs.length > 0) {
      schema.hasOfferCatalog = {
        "@type": "OfferCatalog",
        "name": "Programas de Aprendizado",
        "itemListElement": programs.map((program, index) => ({
          "@type": "EducationalOccupationalProgram",
          "name": program.name,
          "description": program.description,
          "url": program.url,
          "educationalLevel": program.educationalLevel,
          "timeToComplete": program.timeToComplete,
          "position": index + 1
        }))
      };
    }

    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(schemaId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [name, description, url, logo, email, foundingDate, areaServed, knowsLanguage, 
      numberOfStudents, programs]);

  return null;
}

export default EducationalOrganizationSchema;
